import { useCallback, useRef } from 'react';
import { Identifier } from '../../model/prescription/PrescriptionModelReference';

export type PrescriptionJobInitialization = {
  jobId?: number | null;
  fieldId?: number | null;
  maxPrescription?: number | null;
  prescriptionModel?: { identifier?: string } | null;
  metaType?: string | null;
};

export const usePrescriptionJobInitialization = (
  prescriptionJob: PrescriptionJobInitialization | null | undefined
) => {
  const initializedSettings = useRef({
    initialized: !!prescriptionJob?.jobId,
    fieldId: prescriptionJob?.fieldId,
    maxPrescription: prescriptionJob?.maxPrescription,
    adjustPrescriptionBasedOnSoilAnalysis:
      prescriptionJob?.prescriptionModel?.identifier === Identifier.MultiLayerSeedingModel,
    metaType: prescriptionJob?.metaType,
  });

  return useCallback(
    async (
      initializePrescriptionJob: () => void | Promise<unknown>,
      maxPrescription: number | null | undefined,
      adjustPrescriptionBasedOnSoilAnalysis: boolean,
      onLoadingChange: (loading: boolean) => void = () => {}
    ) => {
      const settings = initializedSettings.current;

      if (
        !settings.initialized ||
        settings.fieldId !== prescriptionJob?.fieldId ||
        settings.maxPrescription !== maxPrescription ||
        settings.adjustPrescriptionBasedOnSoilAnalysis !== adjustPrescriptionBasedOnSoilAnalysis ||
        settings.metaType !== prescriptionJob?.metaType
      ) {
        initializedSettings.current = {
          initialized: true,
          fieldId: prescriptionJob?.fieldId,
          maxPrescription,
          adjustPrescriptionBasedOnSoilAnalysis,
          metaType: prescriptionJob?.metaType,
        };
        onLoadingChange(true);
        try {
          await initializePrescriptionJob();
        } catch (error) {
          // Allow the same settings to be retried if setup failed.
          initializedSettings.current = settings;
          throw error;
        } finally {
          onLoadingChange(false);
        }
      }
    },
    [prescriptionJob?.fieldId, prescriptionJob?.metaType]
  );
};
